import { Grid, LinearProgress, Stack } from "@mui/material";
import omit from "lodash/omit";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../admin/products/useProducts";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import { OrderStatus } from "../data/BaseEntity";
import { useQuery } from "../misc/useQuery";
import { findSaveDiscrepancies, type SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import BackToOrderListButton from "./BackToOrderListButton";
import OrderEdit from "./orderEdit/OrderEdit";
import type { NewOrder, SavedOrder } from "./orderTypes";
import { useCreateOrder } from "./useCreateOrder";
import { useOrderWithId } from "./useOrderWithId";

const defaultOrder: () => NewOrder = () => ({
  customerId: "",
  participantsNumber: { numberOfPaid: 1, numberOfStaff: 0 },
  files: [],
  status: OrderStatus.TO_ORDER,
  products: [],
  currency: "EUR",
  name: "",
  info: "",
  infoForSupervisor: "",
  advance: {},
  tags: [],
  type: "DEFAULT",
  cancellationConditionId: "",
});

function clone(order: SavedOrder): NewOrder {
  return omit(
    {
      ...order,
      name: `${order.name} (kopia)`,
      files: [],
      products: order.products
        .map((p) => omit(p, "bookingNumber", "bookingId", "price"))
        .map((p) => ({ ...p, options: p.options.map((o) => omit(o, "price")) })),
    },
    "created",
    "id",
    "invoices",
  );
}

function buildOrderToSubmit(order: NewOrder) {
  const { customerId, customerContactPersonEmail, products, ...other } = order;
  return {
    ...other,
    cancellationConditionId: order.cancellationConditionId ?? undefined,
    customer: {
      id: customerId,
      contactPersonEmail: customerContactPersonEmail,
    },
    products: products.map((p) => ({
      id: p.id,
      date: p.date,
      time: p.time ? p.time : undefined,
      bookingNumber: p.bookingNumber ? p.bookingNumber : undefined,
      note: p.note ? p.note : undefined,
      options: p.options.map((o) => ({
        id: o.id,
        quantity: o.quantity,
        price: o.price && o.price !== "INVALID" ? o.price : undefined,
      })),
    })),
    type: "DEFAULT" as const,
  };
}

const NewOrderPage = () => {
  const navigate = useNavigate();
  const { onCreate } = useCreateOrder();
  const { products } = useProducts();
  const [order, setOrder] = useState<NewOrder>(defaultOrder());
  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);
  const query = useQuery();
  const fromOrderId = query.get("from");
  const { order: existingOrder, isLoading } = useOrderWithId(fromOrderId);
  useEffect(() => {
    if (fromOrderId && existingOrder) {
      setOrder(clone(existingOrder));
    }
    if (!fromOrderId) {
      setOrder(defaultOrder());
    }
  }, [fromOrderId, existingOrder]);

  const navigateToOrder = (id: string) => navigate(`/orders/${id}`);

  const handleSubmit = async () => {
    const totalParticipants = order.participantsNumber.numberOfPaid + order.participantsNumber.numberOfStaff;
    const discrepancies = findSaveDiscrepancies(totalParticipants, order.products, products);
    if (discrepancies) {
      setDiscrepancyModal(discrepancies);
      return;
    }
    const orderToSubmit = buildOrderToSubmit(order);
    const { id } = await onCreate(orderToSubmit);
    navigateToOrder(id);
  };

  const handleDiscrepancyConfirm = async () => {
    const orderToSubmit = buildOrderToSubmit(order);
    const { id } = await onCreate(orderToSubmit);
    setDiscrepancyModal(null);
    navigateToOrder(id);
  };

  const handleDiscrepancyCancel = () => {
    setDiscrepancyModal(null);
  };

  if (fromOrderId && isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }
  const submitDisabled =
    !order.name ||
    !order.cancellationConditionId ||
    order.products.length === 0 ||
    order.products.some((p) => p.options.length === 0) ||
    order.products.some((p) => !p.id) ||
    order.products.flatMap((p) => p.options).some((o) => !o.id);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <OrderEdit
        order={{ ...order, files: [] }}
        role="ADMIN"
        onChange={(o) => setOrder((prev) => ({ ...prev, ...o }))}
      />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <BackToOrderListButton />
          <AsyncLoadingButton disabled={submitDisabled} onClick={handleSubmit}>
            Zapisz
          </AsyncLoadingButton>
        </Stack>
      </Grid>
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        onConfirm={handleDiscrepancyConfirm}
        onCancel={handleDiscrepancyCancel}
      />
    </Grid>
  );
};

export default NewOrderPage;
